import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import TurbineAnimation from "../../components/turbine-animation/TurbineAnimation";
import { chartFont, formatDateTime } from "./wplDashboardHelpers";

const theme = {
  text: "#ffffff",
  muted: "#d3e3dd",
  grid: "#3f7468",
  tooltipBg: "#1b443e",
  border: "#3f7468",
  accentAlert: "#f5894a",
  accentBrand: "#38b97c",
  firstStroke: "#38b97c",
  subsequentStroke: "#f0cd6a",
};

const TOP_TURBINE_COUNT = 8;

const TURBINE_COLUMNS = [
  { key: "turbine_name", label: "Turbine", numeric: false },
  { key: "strike_count", label: "Strikes", numeric: true },
  { key: "outside_iec_count", label: "Outside IEC", numeric: true },
  { key: "subsequent_stroke_count", label: "Subsequent", numeric: true },
  { key: "last_strike_at", label: "Last strike", numeric: false },
];

const tooltipStyles = {
  contentStyle: {
    background: theme.tooltipBg,
    border: `1px solid ${theme.border}`,
    color: theme.text,
  },
  itemStyle: { color: theme.text },
  labelStyle: { color: theme.muted },
};

export default function WplWindfarmDetail({ farm, detail, loading, error, onClose }) {
  const [turbineView, setTurbineView] = useState("chart");
  const [sort, setSort] = useState({ key: "strike_count", dir: "desc" });

  const toggleSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "desc" ? "asc" : "desc" }
        : { key, dir: "desc" }
    );

  const sortedTurbines = useMemo(() => {
    if (!detail) return [];
    const factor = sort.dir === "desc" ? -1 : 1;
    return [...detail.turbines].sort((a, b) => {
      const left = a[sort.key];
      const right = b[sort.key];
      if (left === right) return 0;
      if (left === null || left === undefined) return 1;
      if (right === null || right === undefined) return -1;
      return (left < right ? -1 : 1) * factor;
    });
  }, [detail, sort]);

  const topTurbines = useMemo(() => {
    if (!detail) return [];
    return detail.turbines
      .filter((turbine) => turbine.strike_count > 0)
      .slice(0, TOP_TURBINE_COUNT)
      .map((turbine) => ({
        name: turbine.turbine_name,
        strikes: turbine.strike_count,
        outside_iec: turbine.outside_iec_count,
      }));
  }, [detail]);

  const monthlyData = useMemo(() => {
    if (!detail) return [];
    return detail.monthly.map((entry) => ({
      month: entry.month,
      strikes: entry.strike_count,
    }));
  }, [detail]);

  if (!farm) return null;

  const summary = detail?.summary;
  const strokeTotal = summary
    ? summary.first_strokes + summary.subsequent_strokes
    : 0;
  const firstPct = strokeTotal
    ? Math.round((summary.first_strokes / strokeTotal) * 100)
    : 0;

  return (
    <div className="bd-chart-card bd-windfarm-detail">
      <div className="bd-chart-header bd-detail-header">
        <div>
          <h3>Lightning Highlights — {farm.windfarm_name}</h3>
          <p>
            {farm.owner || farm.customer || "Unknown customer"} · last 12
            months
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            className="bd-modal-close"
            onClick={onClose}
            aria-label="Back to lightning activity"
            title="Back to lightning activity"
          >
            ×
          </button>
        )}
      </div>

      {loading && (
        <TurbineAnimation overlayClassname="bd-windfarms-loading bd-windfarms-loading-small" />
      )}
      {error && (
        <p className="bd-error">
          Could not load windfarm details. Please try again.
        </p>
      )}

      {detail && summary && (
        <>
          <div className="bd-detail-tiles">
            <div className="bd-detail-tile">
              <span className="bd-detail-tile-value">{summary.strikes}</span>
              <span className="bd-detail-tile-label">
                Lightning strikes detected
              </span>
            </div>
            <div className="bd-detail-tile">
              <span className="bd-detail-tile-value">
                {summary.turbines_affected}
              </span>
              <span className="bd-detail-tile-label">
                Turbines with probable attachments
              </span>
            </div>
            <div
              className={`bd-detail-tile${
                summary.outside_iec > 0 ? " bd-detail-tile-alert" : ""
              }`}
            >
              <span className="bd-detail-tile-value">
                {summary.outside_iec}
              </span>
              <span className="bd-detail-tile-label">
                Strikes outside IEC
              </span>
            </div>
          </div>

          {strokeTotal > 0 && (
            <div className="bd-stroke-composition">
              <div className="bd-stroke-composition-header">
                <span>Stroke composition</span>
                <span className="bd-stroke-composition-total">
                  {strokeTotal} total strikes
                </span>
              </div>
              <div className="bd-stroke-bar">
                <div
                  className="bd-stroke-bar-first"
                  style={{ width: `${firstPct}%` }}
                />
                <div
                  className="bd-stroke-bar-subsequent"
                  style={{ width: `${100 - firstPct}%` }}
                />
              </div>
              <div className="bd-stroke-legend">
                <span>
                  <i className="bd-stroke-dot bd-stroke-dot-first" />
                  <b>{summary.first_strokes}</b> first strokes
                </span>
                <span>
                  <i className="bd-stroke-dot bd-stroke-dot-subsequent" />
                  <b>{summary.subsequent_strokes}</b> subsequent strokes
                </span>
              </div>
            </div>
          )}

          {summary.strikes === 0 && (
            <p className="bd-empty">
              No lightning strikes recorded in the last 12 months.
            </p>
          )}

          {detail.turbines.length > 0 && (
            <div className="bd-detail-charts">
              <div className="bd-detail-chart">
                <div className="bd-detail-chart-header">
                  <h4>
                    {turbineView === "chart"
                      ? "Top turbines by lightning attachments"
                      : "Turbines — last 12 months"}
                  </h4>
                  <div className="bd-view-toggle" role="group">
                    <button
                      type="button"
                      className={
                        turbineView === "chart" ? "bd-view-toggle-active" : ""
                      }
                      onClick={() => setTurbineView("chart")}
                    >
                      Chart
                    </button>
                    <button
                      type="button"
                      className={
                        turbineView === "table" ? "bd-view-toggle-active" : ""
                      }
                      onClick={() => setTurbineView("table")}
                    >
                      Table
                    </button>
                  </div>
                </div>

                {turbineView === "table" && (
                  <div className="bd-table-wrapper bd-turbine-table-wrapper">
                    <table className="bd-table">
                      <thead>
                        <tr>
                          {TURBINE_COLUMNS.map((column) => (
                            <th
                              key={column.key}
                              className="bd-th-sortable"
                              onClick={() => toggleSort(column.key)}
                            >
                              {column.label}
                              {sort.key === column.key
                                ? sort.dir === "desc"
                                  ? " ↓"
                                  : " ↑"
                                : ""}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedTurbines.map((turbine) => (
                          <tr key={turbine.turbine_id}>
                            <td>{turbine.turbine_name}</td>
                            <td>{turbine.strike_count}</td>
                            <td
                              className={
                                turbine.outside_iec_count > 0 ? "bd-td-iec" : ""
                              }
                            >
                              {turbine.outside_iec_count || "—"}
                            </td>
                            <td>{turbine.subsequent_stroke_count || "—"}</td>
                            <td>
                              {turbine.last_strike_at
                                ? formatDateTime(turbine.last_strike_at)
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {turbineView === "chart" && topTurbines.length === 0 && (
                  <p className="bd-empty">No struck turbines in this window.</p>
                )}
                {turbineView === "chart" && topTurbines.length > 0 && (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(180, topTurbines.length * 32)}
                >
                  <BarChart
                    data={topTurbines}
                    layout="vertical"
                    margin={{ left: 10, right: 20 }}
                  >
                    <CartesianGrid stroke={theme.grid} horizontal={false} />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fill: theme.muted, fontSize: chartFont(13) }}
                      axisLine={{ stroke: theme.grid }}
                      tickLine={{ stroke: theme.grid }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={90}
                      interval={0}
                      tick={{ fill: theme.muted, fontSize: chartFont(13) }}
                      axisLine={{ stroke: theme.grid }}
                      tickLine={{ stroke: theme.grid }}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(255, 255, 255, 0.08)" }}
                      formatter={(value, key) => [
                        value,
                        key === "outside_iec" ? "Outside IEC" : "Strikes",
                      ]}
                      {...tooltipStyles}
                    />
                    <Bar
                      isAnimationActive={false}
                      dataKey="strikes"
                      fill={theme.accentBrand}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                )}
              </div>

              <div className="bd-detail-chart">
                <h4>Monthly lightning strikes — last 12 months</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={monthlyData} margin={{ left: 10, right: 20 }}>
                    <CartesianGrid stroke={theme.grid} vertical={false} />
                    <XAxis
                      dataKey="month"
                      interval={0}
                      angle={-30}
                      textAnchor="end"
                      height={50}
                      tick={{ fill: theme.muted, fontSize: chartFont(13) }}
                      axisLine={{ stroke: theme.grid }}
                      tickLine={{ stroke: theme.grid }}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: theme.muted, fontSize: chartFont(13) }}
                      axisLine={{ stroke: theme.grid }}
                      tickLine={{ stroke: theme.grid }}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(255, 255, 255, 0.08)" }}
                      formatter={(value) => [value, "Strikes"]}
                      {...tooltipStyles}
                    />
                    <Bar
                      isAnimationActive={false}
                      dataKey="strikes"
                      fill={theme.accentAlert}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
